export const DECODE_TO_JSON_TYPE = {
  Text: 'string',
  Number: 'number',
  Date: 'string',
  'Yes/No': 'boolean',
};

const metricDeclarations = (schema, prefix = '') => {
  const declarations = [];
  Object.entries(schema || {}).forEach(([name, config]) => {
    const path = prefix ? `${prefix}.${name}` : name;
    if (config && typeof config === 'object' && Object.prototype.hasOwnProperty.call(config, 'type')) {
      declarations.push([path, config.type]);
    } else if (config && typeof config === 'object') {
      declarations.push(...metricDeclarations(config, path));
    }
  });
  return declarations;
};

const splitMetricPath = (path) => {
  const parts = path.split('.');
  return parts.length <= 3 ? parts : [parts[0], parts[1], parts.slice(2).join('.')];
};

const decodeType = (declaredType) => (
  Object.prototype.hasOwnProperty.call(DECODE_TO_JSON_TYPE, declaredType)
    ? DECODE_TO_JSON_TYPE[declaredType]
    : 'mixed'
);

const declaredMetricColumns = (metricSchema) => {
  const roots = new Map();
  const order = [];

  metricDeclarations(metricSchema).forEach(([path, declaredType]) => {
    const parts = splitMetricPath(path);
    let cursor = roots;
    parts.forEach((part, index) => {
      if (!cursor.has(part)) {
        cursor.set(part, { children: new Map() });
        if (index === 0) order.push(part);
      }
      const node = cursor.get(part);
      if (index === parts.length - 1) {
        node.type = decodeType(declaredType);
      }
      cursor = node.children;
    });
  });

  const render = (nodes, names) => names.map((name) => {
    const node = nodes.get(name);
    const childNames = Array.from(node.children.keys());
    if (childNames.length > 0) {
      return {
        name,
        type: 'object',
        children: render(node.children, childNames),
      };
    }
    return { name, type: node.type || 'mixed' };
  });

  return render(roots, order);
};

const declaredScalarColumn = (column) => ({
  name: column.name,
  type: decodeType(column.type),
});

export const buildDataSourceManifest = (tableDoc) => {
  const tableType = Object.prototype.hasOwnProperty.call(tableDoc, 'tableType')
    ? tableDoc.tableType
    : 'timeSeries';
  const isRawOrExternal = tableType === 'raw' || tableDoc.storage === 'external';
  const staticColumns = [];
  const identifier = tableDoc.identifier || {};

  if (!isRawOrExternal && identifier.name) {
    staticColumns.push(declaredScalarColumn(identifier));
  }
  ['detailColumns', 'attributeColumns', 'tableAttributeColumns'].forEach((key) => {
    (tableDoc[key] || []).forEach((column) => {
      staticColumns.push(declaredScalarColumn(column));
    });
  });

  const metricColumns = declaredMetricColumns(tableDoc.metricColumns?.schema || {});
  let dynamic = null;
  if (!isRawOrExternal) {
    const timeSeriesConfig = tableDoc.timeSeriesConfig || {};
    const period = Object.prototype.hasOwnProperty.call(timeSeriesConfig, 'period')
      ? timeSeriesConfig.period
      : 'month';
    dynamic = {
      present_at_build: [],
      note: `One object-valued column per reporting ${period}.`,
    };
    if (metricColumns.length > 0) {
      dynamic.children = metricColumns;
    }
  }

  return [{
    key: 'data',
    kind: 'data',
    description: tableDoc.description || tableDoc.name || '',
    schema: {
      static: staticColumns,
      dynamic,
      provenance: 'declared',
    },
  }];
};

const leafPaths = (definitions, prefix = '') => {
  const paths = [];
  (definitions || []).forEach((definition) => {
    const path = prefix ? `${prefix}.${definition.name}` : definition.name;
    const children = definition.type === 'object' ? definition.children : null;
    if (children?.length) {
      paths.push(...leafPaths(children, path));
    } else {
      paths.push(path);
    }
  });
  return paths;
};

export const schemaLeafPaths = (schema, includeDynamic = false) => {
  const paths = leafPaths(schema?.static || []);
  const dynamic = schema?.dynamic;
  if (!includeDynamic || !dynamic) return paths;

  const present = dynamic.present_at_build || [];
  const children = dynamic.children || [];
  if (present.length > 0) {
    present.forEach((name) => {
      paths.push(...(children.length > 0 ? leafPaths(children, name) : [name]));
    });
  } else if (children.length > 0) {
    paths.push(...leafPaths(children));
  }
  return paths;
};
